import CoreUtils from '../utils/CoreUtils';

export default class DataItem {
  constructor(metadataRules) {
    this.key = crypto.randomUUID();

    this.metadataRules = CoreUtils.isDefinedAndNotNull(metadataRules)
      ? metadataRules
      : {};

    this.stateListeners = [];

    this.projectedValues = {};

    this.groupDataItems = [];
    this.groupPropertyNames = [];
    this.groupDataItemStateListener = this.groupDataItemStateUpdated.bind(this);
  }

  dispose() {
    this.groupPropertyNames.forEach(propertyName => {
      delete this[propertyName];
    });

    this.groupPropertyNames = [];

    this.groupDataItems.forEach(dataObject => {
      dataObject.removeStateListener(this.groupDataItemStateListener);
      dataObject.dispose();
    });

    this.groupDataItems = [];

    this.projectedValues = {};

    this.isModified = false;
    this.hasErrors = false;
    this.groupIsModified = false;
    this.groupHasErrors = false;
  }

  attachGroupProperty(propertyName, dataItem) {
    if (CoreUtils.isUndefinedOrNull(dataItem) || !(dataItem instanceof DataItem))
      return;

    if (this[propertyName] !== undefined)
      return;

    this.groupDataItems.push(dataItem);
    this.groupPropertyNames.push(propertyName);

    dataItem.updateProjectedValues(this.projectedValues);
    dataItem.addStateListener(this.groupDataItemStateListener);
    this[propertyName] = dataItem;

    var state = {
      [propertyName]: dataItem
    }

    this.processGroupIsModified(state);
    this.processGroupHasErrors(state);
    this.notifyStateUpdated(state);
  }

  detachGroupProperty(propertyName) {
    var groupPropertyNameIndex = this.groupPropertyNames.indexOf(propertyName);
    if (groupPropertyNameIndex === -1)
      return;

    var dataItem = this[propertyName];
    if (CoreUtils.isUndefinedOrNull(dataItem) || !(dataItem instanceof DataItem))
      return;

    var groupDataItemIndex = this.groupDataItems.indexOf(dataItem);
    if (groupDataItemIndex === -1)
      return;

    delete this[propertyName];
    dataItem.removeStateListener(this.groupDataItemStateListener);
    dataItem.removeProjectedValues(this.projectedValues);

    this.groupPropertyNames = this.groupDataItems.slice(groupPropertyNameIndex);
    this.groupDataItems = this.groupDataItems.slice(groupDataItemIndex);

    var state = {
      [propertyName]: undefined
    }

    this.processGroupIsModified(state);
    this.processGroupHasErrors(state);
    this.notifyStateUpdated(state);
  }

  updateProjectedValues(projectedValues) {
    Object.assign(this.projectedValues, projectedValues);
    this.propogateProjectedValues();
  }

  removeProjectedValues(projectedValues) {
    CoreUtils.getObjectPropertyNames(projectedValues)
      .forEach(propertyName => delete this.projectedValues[propertyName]);
  
    this.groupDataItems.forEach(
      dataItem => dataItem.removeProjectedValues(projectedValues)
    );
  }

  propogateProjectedValues() {
    this.groupDataItems.forEach(
      dataItem => dataItem.updateProjectedValues(this.projectedValues)
    );
  }

  revertItemValues() {
  }

  commitUpdatedValues() {
  }

  revertGroupItemValues() {
    this.groupDataItems.forEach(
      dataObject => dataObject.revertGroupItemValues()
    );

    this.revertItemValues();
  }

  commitGroupItemValues() {
    this.groupDataItems.forEach(
      dataObject => dataObject.commitGroupItemValues()
    );

    this.commitUpdatedValues();
  }

  getState() {
    var state = {
      isModified: this.isModified,
      hasErrors: this.hasErrors,
      groupIsModified: this.groupIsModified,
      groupHasErrors: this.groupHasErrors
    };

    this.groupPropertyNames.forEach(
      propertyName => state[propertyName] = this[propertyName]
    );

    return state;
  }

  processItemIsModified(state) {
    var triggerUpdate = false;

    var isModified = this.resolveItemIsModified();
    if (this.isModified !== isModified) {
      this.isModified = isModified;

      if (CoreUtils.isDefinedAndNotNull(state))
        state.isModified = isModified;

      triggerUpdate = true;
    }

    if (this.processGroupIsModified(state))
      triggerUpdate = true;

    return triggerUpdate;
  }

  resolveItemIsModified() {
    return false;
  }

  groupDataItemStateUpdated() {
    var state = {};

    var updateIsModified = this.processGroupIsModified(state);
    var updateHasErrors = this.processGroupHasErrors(state);

    if (!updateIsModified && !updateHasErrors)
      return;

    this.notifyStateUpdated(state);
  }

  processGroupIsModified(state) {
    var groupIsModified = this.resolveGroupIsModified();
    if (this.groupIsModified === groupIsModified)
      return false;

    this.groupIsModified = groupIsModified;

    if (CoreUtils.isDefinedAndNotNull(state))
      state.groupIsModified = groupIsModified;

    return true;
  }

  resolveGroupIsModified() {
    return this.isModified || this.groupDataItems
      .some(dataObject => dataObject.groupIsModified);
  }

  processItemHasErrors(state) {
    var triggerUpdate = false;

    var hasErrors = this.resolveItemHasErrors();
    if (this.hasErrors !== hasErrors) {
      this.hasErrors = hasErrors;

      if (CoreUtils.isDefinedAndNotNull(state))
        state.hasErrors = hasErrors;

      triggerUpdate = true;
    }

    if (this.processGroupHasErrors(state))
      triggerUpdate = true;

    return triggerUpdate;
  }

  resolveItemHasErrors() {
    return false;
  }

  processGroupHasErrors(state) {
    var groupHasErrors = this.resolveGroupHasErrors();
    if (this.groupHasErrors === groupHasErrors)
      return false;

    this.groupHasErrors = groupHasErrors;

    if (CoreUtils.isDefinedAndNotNull(state))
      state.groupHasErrors = groupHasErrors;

    return true;
  }

  resolveGroupHasErrors() {
    return this.hasErrors || this.groupDataItems
      .some(dataObject => dataObject.groupHasErrors);
  }

  notifyStateUpdated(state) {
    Array.from(this.stateListeners)
      .forEach(listener => listener(state));
  }

  addStateListener(stateListener) {
    if (this.stateListeners.indexOf(stateListener) !== -1)
      return;

    this.stateListeners.push(stateListener);
  }

  removeStateListener(stateListener) {
    var index = this.stateListeners.indexOf(stateListener);

    if (index !== -1)
      this.stateListeners.splice(index, 1);
  }
}